/**
 * OverlayRenderer - Canvas 2D rendering for axes, grid, legend, and cursor
 *
 * This module handles all 2D overlay rendering on top of the WebGL canvas.
 * It uses the theme system for consistent styling.
 */

import type { Scale } from "../scales";
import type { ChartTheme } from "../theme";
import type { Series } from "./Series";
import type { PlotArea, CursorState, AxisOptions } from "../types";
import type { ChartTitleOptions } from "./layout/types";
import type { AxisLayoutOptions } from "./layout/types";
import { formatXTickValue, formatYTickValue } from "./format/axisFormat";
import type { BusinessDayMapping } from "./time/TimeScale";
import { snapLineCoord, snapLabelCoord } from "./render/pixelSnap";

// ============================================
// Overlay Renderer Class
// ============================================

export class OverlayRenderer {
  private ctx: CanvasRenderingContext2D;
  private theme: ChartTheme;
  private latexAPI: any = null;
  private businessDayMapping: BusinessDayMapping | null = null;

  constructor(ctx: CanvasRenderingContext2D, theme: ChartTheme) {
    this.ctx = ctx;
    this.theme = theme;
  }

  setBusinessDayMapping(mapping: BusinessDayMapping | null): void {
    this.businessDayMapping = mapping;
  }

  /** Filter X ticks to valid business-day indices (unique integers in range). */
  private filterBusinessDayXTicks(ticks: number[]): number[] {
    const mapping = this.businessDayMapping;
    if (!mapping) return ticks;
    const maxIdx = mapping.timeByIndex.length - 1;
    if (maxIdx < 0) return [];
    const seen = new Set<number>();
    const result: number[] = [];
    for (const tick of ticks) {
      const idx = Math.round(tick);
      if (idx >= 0 && idx <= maxIdx && !seen.has(idx)) {
        seen.add(idx);
        result.push(idx);
      }
    }
    return result;
  }

  private resolveXTicks(xScale: Scale, tickCount: number): number[] {
    const raw = xScale.ticks(tickCount);
    return this.filterBusinessDayXTicks(raw);
  }

  /**
   * Grid X lines use the full viewport domain (nice even spacing), including
   * regions past the loaded series. Axis labels stay filtered to real data.
   */
  private resolveGridXTicks(xScale: Scale, tickCount: number): number[] {
    return xScale.ticks(tickCount);
  }

  /**
   * Set LaTeX API for rendering mathematical expressions
   */
  setLatexAPI(api: any): void {
    this.latexAPI = api;
  }

  /**
   * Helper to draw text or LaTeX
   */
  private drawLatexOrText(
    text: string,
    x: number,
    y: number,
    options: {
      fontSize: number;
      fontFamily: string;
      fontWeight?: string | number;
      color: string;
      align?: CanvasTextAlign;
      baseline?: CanvasTextBaseline;
      rotation?: number;
    }
  ): void {
    const { ctx, latexAPI } = this;

    // Check if it's LaTeX (heuristic: contains backslash, caret, underscore, or braces)
    const isLatex = !!latexAPI && (typeof latexAPI.render === 'function') && (
      text.includes('\\') ||
      text.includes('^') ||
      text.includes('_') ||
      (text.includes('{') && text.includes('}'))
    );

    if (isLatex) {
      ctx.save();
      ctx.translate(x, y);
      if (options.rotation) ctx.rotate(options.rotation);

      const dims = latexAPI.measure(text, {
        fontSize: options.fontSize,
        fontFamily: options.fontFamily,
        color: options.color
      });

      let lx = 0;
      let ly = 0;

      // Handle Horizontal Alignment
      const align = options.align ?? "left";
      if (align === "center") lx = -dims.width / 2;
      else if (align === "right") lx = -dims.width;

      // Handle Vertical Alignment based on alphabetic baseline of the plugin
      // The plugin draws with textBaseline = 'alphabetic' at the provided y
      const baseline = options.baseline ?? "alphabetic";
      if (baseline === "middle") {
        ly = -dims.height / 2 + dims.baseline;
      } else if (baseline === "top" || baseline === "hanging") {
        ly = dims.baseline;
      } else if (baseline === "bottom" || baseline === "ideographic") {
        ly = -(dims.height - dims.baseline);
      } else {
        // 'alphabetic' or default
        ly = 0;
      }

      latexAPI.render(text, ctx, lx, ly, {
        fontSize: options.fontSize,
        fontFamily: options.fontFamily,
        color: options.color
      });
      ctx.restore();
    } else {
      ctx.save();
      ctx.font = `${options.fontWeight ?? ''} ${options.fontSize}px ${options.fontFamily}`;
      ctx.fillStyle = options.color;
      ctx.textAlign = options.align ?? "left";
      ctx.textBaseline = options.baseline ?? "alphabetic";
      ctx.translate(x, y);
      if (options.rotation) ctx.rotate(options.rotation);
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  }

  /**
   * Update the theme
   */
  setTheme(theme: ChartTheme): void {
    this.theme = theme;
  }

  /**
   * Clear the overlay
   */
  clear(): void {
    const canvas = this.ctx.canvas;
    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.ctx.restore();
  }

  /**
   * Draw the grid
   */
  drawGrid(
    plotArea: PlotArea,
    xScale: Scale,
    yScale: Scale,
    xAxisOptions?: AxisOptions,
    yAxisOptions?: AxisOptions,
  ): void {
    if (!this.theme.grid.visible) return;

    const { ctx } = this;
    const grid = this.theme.grid;

    const xTickCount = xAxisOptions?.tickCount ?? 8;
    const yTickCount = yAxisOptions?.tickCount ?? 6;
    // Symmetric vertical divisions across the whole visible X domain
    // (not clipped to existing business-day bars / loaded series).
    const xTicks = this.resolveGridXTicks(xScale, xTickCount);
    const yTicks = yScale.ticks(yTickCount);

    // Major grid lines — batched into two strokes (vertical + horizontal)
    ctx.strokeStyle = grid.majorColor;
    ctx.lineWidth = grid.majorWidth;
    ctx.setLineDash(grid.majorDash);

    ctx.beginPath();
    xTicks.forEach((tick) => {
      const x = snapLineCoord(xScale.transform(tick));
      if (x >= plotArea.x && x <= plotArea.x + plotArea.width) {
        ctx.moveTo(x, plotArea.y);
        ctx.lineTo(x, plotArea.y + plotArea.height);
      }
    });
    ctx.stroke();

    ctx.beginPath();
    yTicks.forEach((tick) => {
      const y = snapLineCoord(yScale.transform(tick));
      if (y >= plotArea.y && y <= plotArea.y + plotArea.height) {
        ctx.moveTo(plotArea.x, y);
        ctx.lineTo(plotArea.x + plotArea.width, y);
      }
    });
    ctx.stroke();

    // Minor grid lines (if enabled)
    if (grid.showMinor) {
      ctx.strokeStyle = grid.minorColor;
      ctx.lineWidth = grid.minorWidth;
      ctx.setLineDash(grid.minorDash);

      const minorXTicks = this.generateMinorTicks(xTicks, grid.minorDivisions);
      const minorYTicks = this.generateMinorTicks(yTicks, grid.minorDivisions);

      ctx.beginPath();
      minorXTicks.forEach((tick) => {
        const x = snapLineCoord(xScale.transform(tick));
        if (x >= plotArea.x && x <= plotArea.x + plotArea.width) {
          ctx.moveTo(x, plotArea.y);
          ctx.lineTo(x, plotArea.y + plotArea.height);
        }
      });
      ctx.stroke();

      ctx.beginPath();
      minorYTicks.forEach((tick) => {
        const y = snapLineCoord(yScale.transform(tick));
        if (y >= plotArea.y && y <= plotArea.y + plotArea.height) {
          ctx.moveTo(plotArea.x, y);
          ctx.lineTo(plotArea.x + plotArea.width, y);
        }
      });
      ctx.stroke();
    }

    ctx.setLineDash([]);
  }

  /**
   * Draw polar grid (radial circles and angular spokes)
   */
  drawPolarGrid(
    plotArea: PlotArea,
    xScale: Scale,
    yScale: Scale,
    radialDivisions: number = 5,
    angularDivisions: number = 12,
    angleMode: 'degrees' | 'radians' = 'degrees'
  ): void {
    if (!this.theme.grid.visible) return;

    const { ctx } = this;
    const grid = this.theme.grid;

    // The center of a polar plot is ALWAYS (0,0) in data coordinates
    const centerX = xScale.transform(0);
    const centerY = yScale.transform(0);

    // To fill the entire plot area, we find the distance to the furthest corner
    const corners = [
      { x: plotArea.x, y: plotArea.y },
      { x: plotArea.x + plotArea.width, y: plotArea.y },
      { x: plotArea.x, y: plotArea.y + plotArea.height },
      { x: plotArea.x + plotArea.width, y: plotArea.y + plotArea.height }
    ];

    let maxPixelRadius = 0;
    for (const corner of corners) {
      const dist = Math.sqrt(Math.pow(corner.x - centerX, 2) + Math.pow(corner.y - centerY, 2));
      maxPixelRadius = Math.max(maxPixelRadius, dist);
    }

    ctx.save();

    // Clip grid to plot area
    ctx.beginPath();
    ctx.rect(plotArea.x, plotArea.y, plotArea.width, plotArea.height);
    ctx.clip();

    ctx.strokeStyle = grid.majorColor;
    ctx.lineWidth = grid.majorWidth;
    ctx.setLineDash(grid.majorDash);

    // Draw radial circles (concentric)
    // We adjust radial divisions to look good with zoom
    for (let i = 1; i <= radialDivisions; i++) {
      const radiusPixels = (i / radialDivisions) * maxPixelRadius;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radiusPixels, 0, 2 * Math.PI);
      ctx.stroke();
    }

    // Draw angular spokes (radial lines)
    const angleStep = angleMode === 'degrees'
      ? (360 / angularDivisions) * (Math.PI / 180)
      : (2 * Math.PI) / angularDivisions;

    for (let i = 0; i < angularDivisions; i++) {
      const angle = i * angleStep;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + maxPixelRadius * Math.cos(angle),
        centerY - maxPixelRadius * Math.sin(angle) // Subtract because Canvas Y is inverted
      );
      ctx.stroke();
    }

    ctx.restore();
    ctx.setLineDash([]);
  }

  /**
   * Draw X axis with ticks and labels
   */
  drawXAxis(
    plotArea: PlotArea,
    xScale: Scale,
    options?: AxisOptions,
    layout?: AxisLayoutOptions
  ): void {
    const { ctx } = this;
    const axis = this.theme.xAxis;
    const xTickCount = options?.tickCount ?? 8;
    const xTicks = this.resolveXTicks(xScale, xTickCount);
    const axisY = snapLineCoord(plotArea.y + plotArea.height);
    const label = options?.label;
    const showLine = options?.showLine !== false;
    const showTicks = options?.showTicks !== false;
    const showLabels = options?.showLabels !== false;
    const domainSpan = xScale.domain[1] - xScale.domain[0];

    if (showLine) {
      ctx.strokeStyle = axis.lineColor;
      ctx.lineWidth = axis.lineWidth;
      ctx.beginPath();
      ctx.moveTo(plotArea.x, axisY);
      ctx.lineTo(plotArea.x + plotArea.width, axisY);
      ctx.stroke();
    }

    if (showTicks || showLabels) {
      ctx.fillStyle = axis.labelColor;
      ctx.font = `${axis.labelSize}px ${axis.fontFamily}`;

      const visibleTicks: Array<{ tick: number; x: number; label: string }> = [];
      for (const tick of xTicks) {
        const x = snapLabelCoord(xScale.transform(tick));
        if (x >= plotArea.x && x <= plotArea.x + plotArea.width) {
          const tickLabel = showLabels
            ? this.formatXTick(tick, options, domainSpan)
            : "";
          if (!showLabels || tickLabel) {
            visibleTicks.push({ tick, x, label: tickLabel });
          }
        }
      }

      let rotateLabels = false;
      if (showLabels && visibleTicks.length > 1) {
        let maxLabelWidth = 0;
        for (const item of visibleTicks) {
          maxLabelWidth = Math.max(maxLabelWidth, ctx.measureText(item.label).width);
        }
        let minSpacing = Infinity;
        for (let i = 1; i < visibleTicks.length; i++) {
          minSpacing = Math.min(minSpacing, visibleTicks[i].x - visibleTicks[i - 1].x);
        }
        rotateLabels = maxLabelWidth + 8 > minSpacing;
      }

      const labelY = axisY + axis.tickLength + (rotateLabels ? 6 : 3);
      const labelRotation = rotateLabels ? -Math.PI / 4 : undefined;

      for (const item of visibleTicks) {
        if (showTicks) {
          ctx.strokeStyle = axis.tickColor;
          ctx.beginPath();
          ctx.moveTo(item.x, axisY);
          ctx.lineTo(item.x, axisY + axis.tickLength);
          ctx.stroke();
        }
        if (showLabels && item.label) {
          this.drawLatexOrText(
            item.label,
            item.x,
            labelY,
            {
              fontSize: axis.labelSize,
              fontFamily: axis.fontFamily,
              color: axis.labelColor,
              align: rotateLabels ? "right" : "center",
              baseline: rotateLabels ? "middle" : "top",
              rotation: labelRotation,
            },
          );
        }
      }
    }

    if (label) {
      const titleGap = layout?.titleGap ?? 45;
      this.drawLatexOrText(label, plotArea.x + plotArea.width / 2, plotArea.y + plotArea.height + titleGap, {
        fontSize: axis.titleSize,
        fontFamily: axis.fontFamily,
        color: axis.titleColor,
        align: "center",
        baseline: "bottom",
      });
    }
  }

  /**
   * Draw Y axis with ticks and labels
   */
  drawYAxis(
    plotArea: PlotArea,
    yScale: Scale,
    options?: AxisOptions,
    position: "left" | "right" = "left",
    offset: number = 0,
    layout?: AxisLayoutOptions
  ): void {
    const { ctx } = this;
    const axis = this.theme.yAxis;
    const yTickCount = options?.tickCount ?? 6;
    const yTicks = yScale.ticks(yTickCount);
    const label = options?.label;
    const showLine = options?.showLine !== false;
    const showTicks = options?.showTicks !== false;
    const showLabels = options?.showLabels !== false;
    const axisX = snapLineCoord(position === 'left' ? plotArea.x - offset : plotArea.x + plotArea.width + offset);
    const tickDir = position === 'left' ? -1 : 1;

    if (showLine) {
      ctx.strokeStyle = axis.lineColor;
      ctx.lineWidth = axis.lineWidth;
      ctx.beginPath();
      ctx.moveTo(axisX, plotArea.y);
      ctx.lineTo(axisX, plotArea.y + plotArea.height);
      ctx.stroke();
    }

    if (showTicks || showLabels) {
      ctx.fillStyle = axis.labelColor;
      ctx.font = `${axis.labelSize}px ${axis.fontFamily}`;
      ctx.textAlign = position === 'left' ? "right" : "left";
      ctx.textBaseline = "middle";

      yTicks.forEach((tick) => {
        const y = snapLabelCoord(yScale.transform(tick));
        if (y >= plotArea.y && y <= plotArea.y + plotArea.height) {
          if (showTicks) {
            ctx.strokeStyle = axis.tickColor;
            ctx.beginPath();
            ctx.moveTo(axisX, y);
            ctx.lineTo(axisX + axis.tickLength * tickDir, y);
            ctx.stroke();
          }
          if (showLabels) {
            const labelX = axisX + (axis.tickLength + 3) * tickDir;
            this.drawLatexOrText(this.formatYTick(tick, options), labelX, y, {
              fontSize: axis.labelSize,
              fontFamily: axis.fontFamily,
              color: axis.labelColor,
              align: position === 'left' ? "right" : "left",
              baseline: "middle",
            });
          }
        }
      });
    }

    if (label) {
      const titleGap = layout?.titleGap ?? 50;
      const titleX = position === 'left' ? axisX - titleGap : axisX + titleGap;
      const titleY = plotArea.y + plotArea.height / 2;
      this.drawLatexOrText(label, titleX, titleY, {
        fontSize: axis.titleSize,
        fontFamily: axis.fontFamily,
        color: axis.titleColor,
        align: "center",
        baseline: "top",
        rotation: position === 'left' ? -Math.PI / 2 : Math.PI / 2,
      });
    }
  }

  /**
   * Draw plot area border
   */
  drawPlotBorder(plotArea: PlotArea): void {
    const { ctx } = this;
    ctx.strokeStyle = this.theme.plotBorderColor;
    ctx.lineWidth = 1;
    ctx.strokeRect(plotArea.x, plotArea.y, plotArea.width, plotArea.height);
  }

  /**
   * Draw chart title
   */
  drawChartTitle(plotArea: PlotArea, options: ChartTitleOptions): void {
    if (!options.visible || !options.text) return;

    const { ctx } = this;
    const fontSize = options.fontSize ?? 16;
    const fontFamily = options.fontFamily ?? "Inter, system-ui, sans-serif";
    const fontWeight = options.fontWeight ?? 600;
    const color = options.color ?? "#ffffff";

    ctx.save();
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textAlign = options.align ?? "center";
    ctx.textBaseline = options.position === "bottom" ? "top" : "bottom";

    // Calculate position
    let x: number;
    let y: number;

    // X alignment
    if (options.align === "left") {
      x = plotArea.x;
    } else if (options.align === "right") {
      x = plotArea.x + plotArea.width;
    } else {
      x = plotArea.x + plotArea.width / 2;
    }

    // Y positioning
    const padding = typeof options.padding === "number" ? options.padding : 10;
    const padTop = (options.padding as any)?.top ?? padding;
    const padBottom = (options.padding as any)?.bottom ?? padding;

    if (options.position === "bottom") {
      y = plotArea.y + plotArea.height + 45 + padTop; // Below X-axis title
    } else {
      y = plotArea.y - padBottom; // Above plot area
    }

    this.drawLatexOrText(options.text, x, y, {
      fontSize,
      fontFamily,
      fontWeight,
      color,
      align: options.align ?? "center",
      baseline: options.position === "bottom" ? "top" : "bottom",
    });
    ctx.restore();
  }

  /**
   * Draw legend
   */
  drawLegend(plotArea: PlotArea, series: Series[]): void {
    if (series.length === 0) return;

    const { ctx } = this;
    const legend = this.theme.legend;

    // Calculate legend dimensions
    ctx.font = `${legend.fontSize}px ${legend.fontFamily}`;
    let maxWidth = 0;
    const items = series.map((s) => {
      const label = s.getId();
      const style = s.getStyle();

      const isLatex = this.latexAPI && (label.includes('\\') || label.includes('^') || label.includes('_'));
      let width = 0;
      if (isLatex) {
        width = this.latexAPI.measure(label, { fontSize: legend.fontSize }).width;
      } else {
        width = ctx.measureText(label).width;
      }

      maxWidth = Math.max(maxWidth, width);
      return {
        id: s.getId(),
        color: style.color ?? "#ff0055",
        label,
        type: s.getType(),
        symbol: style.symbol,
        opacity: style.opacity ?? 1
      };
    });

    const boxWidth = legend.swatchSize + 8 + maxWidth + legend.padding * 2;
    const boxHeight =
      items.length * (legend.swatchSize + legend.itemGap) -
      legend.itemGap +
      legend.padding * 2;

    // Calculate position
    let x: number, y: number;
    switch (legend.position) {
      case "top-left":
        x = plotArea.x + 10;
        y = plotArea.y + 10;
        break;
      case "bottom-left":
        x = plotArea.x + 10;
        y = plotArea.y + 10;
        break;
      case "bottom-right":
        x = plotArea.x + plotArea.width - boxWidth - 10;
        y = plotArea.y + plotArea.height - boxHeight - 10;
        break;
      case "top-right":
      default:
        x = plotArea.x + plotArea.width - boxWidth - 10;
        y = plotArea.y + 10;
        break;
    }

    // Draw background
    ctx.fillStyle = legend.backgroundColor;
    ctx.strokeStyle = legend.borderColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const r = legend.borderRadius;
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + boxWidth - r, y);
    ctx.arcTo(x + boxWidth, y, x + boxWidth, y + r, r);
    ctx.lineTo(x + boxWidth, y + boxHeight - r);
    ctx.arcTo(x + boxWidth, y + boxHeight, x + boxWidth - r, y + boxHeight, r);
    ctx.lineTo(x + r, y + boxHeight);
    ctx.arcTo(x, y + boxHeight, x, y + boxHeight - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw items
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    items.forEach((item, i) => {
      const itemY =
        y + legend.padding + i * (legend.swatchSize + legend.itemGap);
      const swatchX = x + legend.padding;
      const centerY = itemY + legend.swatchSize / 2;
      const centerX = swatchX + legend.swatchSize / 2;

      // Draw swatch (symbol or line)
      ctx.save();
      ctx.globalAlpha = item.opacity;
      ctx.fillStyle = item.color;
      ctx.strokeStyle = item.color;
      ctx.lineWidth = 2;

      const size = legend.swatchSize;

      // EXTREME FALLBACK DETECTION
      const typeStr = String(item.type).toLowerCase();
      const hasSymbol = !!item.symbol && item.symbol !== 'circle';

      const isScatter = typeStr === 'scatter' || typeStr === '1' || (typeStr === 'line' && hasSymbol);
      const isLineScatter = typeStr.includes('scatter') || typeStr === '2';

      if (isScatter) {
        this.drawLegendSymbol(ctx, item.symbol ?? 'circle', centerX, centerY, size * 0.9);
      } else if (isLineScatter) {
        // Line + Scatter
        ctx.beginPath();
        ctx.moveTo(swatchX, centerY);
        ctx.lineTo(swatchX + size, centerY);
        ctx.stroke();

        this.drawLegendSymbol(ctx, item.symbol ?? 'circle', centerX, centerY, size * 0.6);
      } else {
        // Pure line or step
        ctx.beginPath();
        ctx.moveTo(swatchX, centerY);
        ctx.lineTo(swatchX + size, centerY);
        ctx.stroke();
      }
      ctx.restore();

      // Label
      this.drawLatexOrText(item.label, x + legend.padding + legend.swatchSize + 8, centerY, {
        fontSize: legend.fontSize,
        fontFamily: legend.fontFamily,
        color: legend.textColor,
        align: "left",
        baseline: "middle",
      });
    });
  }

  /**
   * Helper to draw a symbol in the legend
   */
  private drawLegendSymbol(
    ctx: CanvasRenderingContext2D,
    symbol: string,
    x: number,
    y: number,
    size: number
  ): void {
    const r = size / 2;
    ctx.beginPath();

    switch (symbol) {
      case 'circle':
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'square':
        ctx.rect(x - r, y - r, size, size);
        ctx.fill();
        break;
      case 'diamond':
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x, y + r);
        ctx.lineTo(x - r, y);
        ctx.closePath();
        ctx.fill();
        break;
      case 'triangle':
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y + r);
        ctx.lineTo(x - r, y + r);
        ctx.closePath();
        ctx.fill();
        break;
      case 'triangleDown':
        ctx.moveTo(x, y + r);
        ctx.lineTo(x + r, y - r);
        ctx.lineTo(x - r, y - r);
        ctx.closePath();
        ctx.fill();
        break;
      case 'cross':
        ctx.moveTo(x - r, y);
        ctx.lineTo(x + r, y);
        ctx.moveTo(x, y - r);
        ctx.lineTo(x, y + r);
        ctx.stroke();
        break;
      case 'x':
        const d = r * 0.707; // sin(45)
        ctx.moveTo(x - d, y - d);
        ctx.lineTo(x + d, y + d);
        ctx.moveTo(x + d, y - d);
        ctx.lineTo(x - d, y + d);
        ctx.stroke();
        break;
      case 'star':
        for (let i = 0; i < 5; i++) {
          ctx.lineTo(
            x + r * Math.cos(((18 + i * 72) / 180) * Math.PI),
            y - r * Math.sin(((18 + i * 72) / 180) * Math.PI)
          );
          ctx.lineTo(
            x + (r / 2) * Math.cos(((54 + i * 72) / 180) * Math.PI),
            y - (r / 2) * Math.sin(((54 + i * 72) / 180) * Math.PI)
          );
        }
        ctx.closePath();
        ctx.fill();
        break;
    }
  }

  /**
   * Draw cursor/crosshair
   */
  drawCursor(plotArea: PlotArea, cursor: CursorState, lineStyle?: 'solid' | 'dashed' | 'dotted'): void {
    if (!cursor.enabled) return;

    const { ctx } = this;
    const cursorTheme = this.theme.cursor;

    // Check if cursor is in plot area
    if (
      cursor.x < plotArea.x ||
      cursor.x > plotArea.x + plotArea.width ||
      cursor.y < plotArea.y ||
      cursor.y > plotArea.y + plotArea.height
    ) {
      return;
    }

    ctx.save();

    // Clip to plot area
    ctx.beginPath();
    ctx.rect(plotArea.x, plotArea.y, plotArea.width, plotArea.height);
    ctx.clip();

    // Crosshair style
    ctx.strokeStyle = cursorTheme.lineColor;
    ctx.lineWidth = cursorTheme.lineWidth;

    // Apply line style
    const style = lineStyle ?? 'dashed';
    if (style === 'dashed') {
      ctx.setLineDash(cursorTheme.lineDash);
    } else if (style === 'dotted') {
      ctx.setLineDash([2, 2]);
    } else {
      ctx.setLineDash([]);
    }

    if (cursor.crosshair) {
      // Vertical line
      ctx.beginPath();
      ctx.moveTo(cursor.x, plotArea.y);
      ctx.lineTo(cursor.x, plotArea.y + plotArea.height);
      ctx.stroke();

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(plotArea.x, cursor.y);
      ctx.lineTo(plotArea.x + plotArea.width, cursor.y);
      ctx.stroke();
    } else {
      // Just vertical line
      ctx.beginPath();
      ctx.moveTo(cursor.x, plotArea.y);
      ctx.lineTo(cursor.x, plotArea.y + plotArea.height);
      ctx.stroke();
    }

    ctx.restore();

    // Draw tooltip based on valueDisplayMode
    if (cursor.tooltipText) {
      const valueDisplayMode = cursor.valueDisplayMode ?? 'floating';
      if (valueDisplayMode === 'corner') {
        this.drawCornerTooltip(cursor.tooltipText, plotArea, cursor.cornerPosition ?? 'top-left');
      } else if (valueDisplayMode === 'floating') {
        this.drawTooltip(cursor.x, cursor.y, cursor.tooltipText, plotArea);
      }
      // 'disabled' mode: no tooltip drawn
    }
  }

  /**
   * Draw tooltip
   */
  private drawTooltip(
    x: number,
    y: number,
    text: string,
    plotArea: PlotArea
  ): void {
    const { ctx } = this;
    const cursor = this.theme.cursor;
    const lines = text.split("\n");
    const lineHeight = cursor.tooltipSize + 5;
    const padding = 8;

    let maxWidth = 0;
    const lineHeights: number[] = [];
    let totalContentHeight = 0;
    lines.forEach((line) => {
      const isLatex = !!this.latexAPI && (line.includes('\\') || line.includes('^') || line.includes('_') || (line.includes('{') && line.includes('}')));
      if (isLatex) {
        const dims = this.latexAPI.measure(line, { fontSize: cursor.tooltipSize });
        maxWidth = Math.max(maxWidth, dims.width);
        const h = Math.max(lineHeight, dims.height);
        lineHeights.push(h);
        totalContentHeight += h;
      } else {
        maxWidth = Math.max(maxWidth, ctx.measureText(line).width);
        lineHeights.push(lineHeight);
        totalContentHeight += lineHeight;
      }
    });

    const boxWidth = maxWidth + padding * 2;
    const boxHeight = totalContentHeight + padding * 2 - 4;

    // Position tooltip
    let tooltipX = x + 15;
    let tooltipY = y - boxHeight - 10;

    if (tooltipX + boxWidth > plotArea.x + plotArea.width) {
      tooltipX = x - boxWidth - 15;
    }
    if (tooltipY < plotArea.y) {
      tooltipY = y + 15;
    }

    // Background
    ctx.fillStyle = cursor.tooltipBackground;
    ctx.strokeStyle = cursor.tooltipBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const r = 4;
    ctx.moveTo(tooltipX + r, tooltipY);
    ctx.lineTo(tooltipX + boxWidth - r, tooltipY);
    ctx.arcTo(tooltipX + boxWidth, tooltipY, tooltipX + boxWidth, tooltipY + r, r);
    ctx.lineTo(tooltipX + boxWidth, tooltipY + boxHeight - r);
    ctx.arcTo(tooltipX + boxWidth, tooltipY + boxHeight, tooltipX + boxWidth - r, tooltipY + boxHeight, r);
    ctx.lineTo(tooltipX + r, tooltipY + boxHeight);
    ctx.arcTo(tooltipX, tooltipY + boxHeight, tooltipX, tooltipY + boxHeight - r, r);
    ctx.lineTo(tooltipX, tooltipY + r);
    ctx.arcTo(tooltipX, tooltipY, tooltipX + r, tooltipY, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Text
    let currentY = tooltipY + padding;
    lines.forEach((line, i) => {
      this.drawLatexOrText(line, tooltipX + padding, currentY, {
        fontSize: cursor.tooltipSize,
        fontFamily: this.theme.xAxis.fontFamily,
        color: cursor.tooltipColor,
        align: "left",
        baseline: "top",
      });
      currentY += lineHeights[i];
    });
  }

  /**
   * Draw tooltip in a fixed corner position
   */
  private drawCornerTooltip(
    text: string,
    plotArea: PlotArea,
    cornerPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  ): void {
    const { ctx } = this;
    const cursor = this.theme.cursor;
    const lines = text.split("\n");
    const lineHeight = cursor.tooltipSize + 5;
    const padding = 8;
    const margin = 10;

    let maxWidth = 0;
    const lineHeights: number[] = [];
    let totalContentHeight = 0;
    lines.forEach((line) => {
      const isLatex = !!this.latexAPI && (line.includes('\\') || line.includes('^') || line.includes('_') || (line.includes('{') && line.includes('}')));
      if (isLatex) {
        const dims = this.latexAPI.measure(line, { fontSize: cursor.tooltipSize });
        maxWidth = Math.max(maxWidth, dims.width);
        const h = Math.max(lineHeight, dims.height);
        lineHeights.push(h);
        totalContentHeight += h;
      } else {
        maxWidth = Math.max(maxWidth, ctx.measureText(line).width);
        lineHeights.push(lineHeight);
        totalContentHeight += lineHeight;
      }
    });

    const boxWidth = maxWidth + padding * 2;
    const boxHeight = totalContentHeight + padding * 2 - 4;

    // Calculate position based on corner
    let tooltipX: number;
    let tooltipY: number;

    switch (cornerPosition) {
      case 'top-left':
        tooltipX = plotArea.x + margin;
        tooltipY = plotArea.y + margin;
        break;
      case 'top-right':
        tooltipX = plotArea.x + plotArea.width - boxWidth - margin;
        tooltipY = plotArea.y + margin;
        break;
      case 'bottom-left':
        tooltipX = plotArea.x + margin;
        tooltipY = plotArea.y + plotArea.height - boxHeight - margin;
        break;
      case 'bottom-right':
        tooltipX = plotArea.x + plotArea.width - boxWidth - margin;
        tooltipY = plotArea.y + plotArea.height - boxHeight - margin;
        break;
    }

    // Background
    ctx.fillStyle = cursor.tooltipBackground;
    ctx.strokeStyle = cursor.tooltipBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const r = 4;
    ctx.moveTo(tooltipX + r, tooltipY);
    ctx.lineTo(tooltipX + boxWidth - r, tooltipY);
    ctx.arcTo(tooltipX + boxWidth, tooltipY, tooltipX + boxWidth, tooltipY + r, r);
    ctx.lineTo(tooltipX + boxWidth, tooltipY + boxHeight - r);
    ctx.arcTo(tooltipX + boxWidth, tooltipY + boxHeight, tooltipX + boxWidth - r, tooltipY + boxHeight, r);
    ctx.lineTo(tooltipX + r, tooltipY + boxHeight);
    ctx.arcTo(tooltipX, tooltipY + boxHeight, tooltipX, tooltipY + boxHeight - r, r);
    ctx.lineTo(tooltipX, tooltipY + r);
    ctx.arcTo(tooltipX, tooltipY, tooltipX + r, tooltipY, r);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Text
    let currentY = tooltipY + padding;
    lines.forEach((line, i) => {
      this.drawLatexOrText(line, tooltipX + padding, currentY, {
        fontSize: cursor.tooltipSize,
        fontFamily: this.theme.xAxis.fontFamily,
        color: cursor.tooltipColor,
        align: "left",
        baseline: "top",
      });
      currentY += lineHeights[i];
    });
  }

  /**
   * Draw selection rectangle (Box Zoom)
   */
  drawSelectionRect(rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): void {
    const { ctx } = this;
    const isDark =
      this.theme.name.toLowerCase().includes("dark") ||
      this.theme.name.toLowerCase().includes("midnight");

    ctx.save();
    ctx.fillStyle = isDark
      ? "rgba(0, 170, 255, 0.15)"
      : "rgba(0, 100, 255, 0.1)";
    ctx.strokeStyle = "#00aaff";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.width, rect.height);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Draw error bars for a series
   */
  drawErrorBars(
    plotArea: PlotArea,
    series: Series,
    xScale: Scale,
    yScale: Scale
  ): void {
    if (!series.hasErrorData()) return;

    const { ctx } = this;
    const data = series.getData();
    const style = series.getStyle();
    const errorStyle = style.errorBars ?? {};

    // Skip if explicitly hidden
    if (errorStyle.visible === false) return;

    // Error bar styling
    const color = errorStyle.color ?? style.color ?? '#ff0055';
    const lineWidth = errorStyle.width ?? 1;
    const capWidth = errorStyle.capWidth ?? 6;
    const showCaps = errorStyle.showCaps !== false;
    const opacity = errorStyle.opacity ?? 0.7;
    const direction = errorStyle.direction ?? 'both';

    ctx.save();

    // Clip to plot area
    ctx.beginPath();
    ctx.rect(plotArea.x, plotArea.y, plotArea.width, plotArea.height);
    ctx.clip();

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = opacity;

    // Draw error bars for each point
    for (let i = 0; i < data.x.length; i++) {
      const x = xScale.transform(data.x[i]);
      const y = yScale.transform(data.y[i]);

      // Skip points outside plot area
      if (x < plotArea.x || x > plotArea.x + plotArea.width) continue;
      if (y < plotArea.y || y > plotArea.y + plotArea.height) continue;

      // Y error bars (vertical)
      const yError = series.getYError(i);
      if (yError) {
        const [errorMinus, errorPlus] = yError;
        const yBase = data.y[i];

        // Convert error values to pixel positions
        const yTop = yScale.transform(yBase + errorPlus);
        const yBottom = yScale.transform(yBase - errorMinus);

        ctx.beginPath();

        // Draw based on direction
        if (direction === 'both' || direction === 'positive') {
          // Upper error bar
          ctx.moveTo(x, y);
          ctx.lineTo(x, yTop);
          // Top cap
          if (showCaps) {
            ctx.moveTo(x - capWidth / 2, yTop);
            ctx.lineTo(x + capWidth / 2, yTop);
          }
        }

        if (direction === 'both' || direction === 'negative') {
          // Lower error bar
          ctx.moveTo(x, y);
          ctx.lineTo(x, yBottom);
          // Bottom cap
          if (showCaps) {
            ctx.moveTo(x - capWidth / 2, yBottom);
            ctx.lineTo(x + capWidth / 2, yBottom);
          }
        }

        ctx.stroke();
      }

      // X error bars (horizontal)
      const xError = series.getXError(i);
      if (xError) {
        const [errorMinus, errorPlus] = xError;
        const xBase = data.x[i];

        // Convert error values to pixel positions
        const xRight = xScale.transform(xBase + errorPlus);
        const xLeft = xScale.transform(xBase - errorMinus);

        ctx.beginPath();

        if (direction === 'both' || direction === 'positive') {
          // Right error bar
          ctx.moveTo(x, y);
          ctx.lineTo(xRight, y);
          // Right cap
          if (showCaps) {
            ctx.moveTo(xRight, y - capWidth / 2);
            ctx.lineTo(xRight, y + capWidth / 2);
          }
        }

        if (direction === 'both' || direction === 'negative') {
          // Left error bar
          ctx.moveTo(x, y);
          ctx.lineTo(xLeft, y);
          // Left cap
          if (showCaps) {
            ctx.moveTo(xLeft, y - capWidth / 2);
            ctx.lineTo(xLeft, y + capWidth / 2);
          }
        }

        ctx.stroke();
      }
    }

    ctx.restore();
  }

  // ----------------------------------------
  // Helper Methods
  // ----------------------------------------

  private generateMinorTicks(
    majorTicks: number[],
    divisions: number
  ): number[] {
    if (majorTicks.length < 2) return [];

    const minor: number[] = [];
    for (let i = 0; i < majorTicks.length - 1; i++) {
      const step = (majorTicks[i + 1] - majorTicks[i]) / divisions;
      for (let j = 1; j < divisions; j++) {
        minor.push(majorTicks[i] + step * j);
      }
    }
    return minor;
  }

  private formatXTick(value: number, options?: AxisOptions, domainSpan?: number): string {
    return formatXTickValue(value, options, domainSpan, this.businessDayMapping);
  }

  private formatYTick(value: number, options?: AxisOptions): string {
    return formatYTickValue(value, options);
  }

  /** Draw buy/sell markers on candlestick series (Stage 2.14). */
  drawCandlestickMarkers(
    plotArea: PlotArea,
    series: Series,
    xScale: Scale,
    yScale: Scale,
  ): void {
    const markers = series.getMarkers?.() ?? [];
    if (!markers.length || series.getType() !== "candlestick") return;

    const data = series.getData();
    const ctx = this.ctx;
    const xArr = data.x;
    const high = data.high ?? data.y;
    const low = data.low ?? data.y;
    const close = data.close ?? data.y;

    for (const m of markers) {
      const idx = (() => {
        let best = 0;
        let bestDist = Math.abs(xArr[0] - m.time);
        for (let i = 1; i < xArr.length; i++) {
          const d = Math.abs(xArr[i] - m.time);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        }
        return best;
      })();

      const position = m.position ?? "aboveBar";
      const yVal =
        position === "belowBar"
          ? low[idx]
          : position === "inBar"
            ? close[idx]
            : high[idx];

      const px = plotArea.x + xScale.transform(xArr[idx]);
      const py = plotArea.y + yScale.transform(yVal);
      const color = m.color ?? "#22c55e";
      const offset = position === "belowBar" ? 10 : -10;

      ctx.save();
      ctx.fillStyle = color;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;

      if (m.shape === "arrowDown") {
        ctx.beginPath();
        ctx.moveTo(px, py - offset);
        ctx.lineTo(px - 5, py - offset - 8);
        ctx.lineTo(px + 5, py - offset - 8);
        ctx.closePath();
        ctx.fill();
      } else if (m.shape === "circle") {
        ctx.beginPath();
        ctx.arc(px, py + offset, 4, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(px, py + offset);
        ctx.lineTo(px - 5, py + offset + 8);
        ctx.lineTo(px + 5, py + offset + 8);
        ctx.closePath();
        ctx.fill();
      }

      if (m.text) {
        ctx.font = "10px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(m.text, px, py + offset + (position === "belowBar" ? 20 : -14));
      }
      ctx.restore();
    }
  }

  /** Horizontal dashed lines for active price alerts (Stage 2.19). */
  drawPriceAlertLines(
    plotArea: PlotArea,
    alerts: Array<{ price: number; direction?: string }>,
    yScale: Scale,
  ): void {
    if (!alerts.length) return;
    const { ctx } = this;
    ctx.save();
    for (const alert of alerts) {
      const py = snapLabelCoord(yScale.transform(alert.price));
      if (py < plotArea.y || py > plotArea.y + plotArea.height) continue;
      ctx.strokeStyle =
        alert.direction === "below"
          ? "rgba(239, 68, 68, 0.7)"
          : alert.direction === "above"
            ? "rgba(34, 197, 94, 0.7)"
            : "rgba(250, 204, 21, 0.8)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(plotArea.x, py);
      ctx.lineTo(plotArea.x + plotArea.width, py);
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.restore();
  }
}
